import { useTranslation as useI18nTranslation } from 'react-i18next';
import { I18N_MANUAL_SELECTION_KEY } from '../constants/i18n';

type TranslationOptions = {
  [key: string]: string | number;
};

interface UseTranslationReturn {
  t: (key: string, options?: TranslationOptions | { returnObjects: boolean }) => any;
  changeLanguage: (language: string) => Promise<void>;
  currentLanguage: string;
  isLoading: boolean;
}

/**
 * Custom hook for translations with type safety and additional utilities
 * Follows functional patterns and provides a clean API for i18n
 */
export const useTranslation = (): UseTranslationReturn => {
  const { t, i18n, ready } = useI18nTranslation();

  const translate = (key: string, options?: TranslationOptions | { returnObjects: boolean }): any => {
    return t(key, options);
  };

  const changeLanguage = async (language: string): Promise<void> => {
    try {
      await i18n.changeLanguage(language);
      // Mark this as a manual selection by the user
      localStorage.setItem(I18N_MANUAL_SELECTION_KEY, 'true');
    } catch (error) {
      console.error('Error changing language:', error);
    }
  };

  return {
    t: translate,
    changeLanguage,
    currentLanguage: i18n.language,
    isLoading: !ready
  };
};

// Helper functions for common translation patterns
export const getNavigationTranslations = (t: (key: string) => string) => ({
  services: t('navigation.services'),
  pricing: t('navigation.pricing'),
  about: t('navigation.about'),
  contact: t('navigation.contact'),
  login: t('navigation.login'),
  register: t('navigation.register'),
  dashboard: t('navigation.dashboard'),
  logout: t('navigation.logout')
});

export const getCommonTranslations = (t: (key: string) => string) => ({
  loading: t('common.loading'),
  error: t('common.error'),
  success: t('common.success'),
  cancel: t('common.cancel'),
  save: t('common.save'),
  edit: t('common.edit'),
  delete: t('common.delete'),
  search: t('common.search'),
  filter: t('common.filter'),
  clear: t('common.clear'),
  back: t('common.back'),
  next: t('common.next'),
  previous: t('common.previous'),
  close: t('common.close'),
  open: t('common.open'),
  view: t('common.view'),
  download: t('common.download'),
  upload: t('common.upload'),
  send: t('common.send'),
  submit: t('common.submit'),
  confirm: t('common.confirm'),
  yes: t('common.yes'),
  no: t('common.no')
});
